import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { torcidaApi } from './torcidaApi';
import { API } from '../../contexts/AuthContext';
import logoCentrao from '../../assets/logo-centrao.png';
import './torcida.css';

const POLL_MS = 30000; // recarrega dados/ranking a cada 30s

// Lista de slides a exibir. Usa a config do marketing (dados.slides, já filtrada por
// ativo no backend); senão, fallback pro ciclo padrão antigo (convite, ranking, resultado).
function montarSlides(dados) {
  if (!dados) return [];
  if (Array.isArray(dados.slides) && dados.slides.length) {
    // pula o slide de resultado quando ainda não há jogo finalizado
    return dados.slides.filter((s) => s.tipo !== 'resultado' || dados.ultimo_resultado);
  }
  const fb = [
    { tipo: 'convite', duracao_seg: 30 },
    { tipo: 'ranking', duracao_seg: 30, ranking_pos: 'centro' },
  ];
  if (dados.ultimo_resultado) fb.push({ tipo: 'resultado', duracao_seg: 30 });
  return fb;
}

export default function TorcidaTv() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // ?modo=N trava no slide de índice N (preview/ajuste). Sem o parâmetro, cicla normal.
  const idxFixo = searchParams.get('modo');
  const [dados, setDados] = useState(null);
  const [idx, setIdx] = useState(0);
  const [erro, setErro] = useState('');
  const [agora, setAgora] = useState(Date.now());

  const carregar = useCallback(async () => {
    try {
      const r = await torcidaApi.tv(slug);
      setDados(r.data);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível carregar a TV.');
    }
  }, [slug]);

  useEffect(() => { carregar(); const id = setInterval(carregar, POLL_MS); return () => clearInterval(id); }, [carregar]);

  const slides = useMemo(() => montarSlides(dados), [dados]);

  // Loop dos slides: cada um fica no ar pelo seu duracao_seg. Travado se ?modo= presente.
  useEffect(() => {
    if (idxFixo != null || !slides.length) return;
    const atual = slides[Math.min(idx, slides.length - 1)];
    const ms = Math.max(3, parseInt(atual?.duracao_seg, 10) || 30) * 1000;
    const id = setTimeout(() => setIdx((i) => (i + 1) % slides.length), ms);
    return () => clearTimeout(id);
  }, [idx, slides, idxFixo]);

  // Tick pro countdown
  useEffect(() => { const id = setInterval(() => setAgora(Date.now()), 1000); return () => clearInterval(id); }, []);

  // Recarrega a página completa a cada 1 minuto (mantém a TV estável em sets que acumulam memória)
  useEffect(() => { const id = setTimeout(() => window.location.reload(), 60000); return () => clearTimeout(id); }, []);

  // Tela cheia automática no 1º gesto (clique/toque/tecla) — navegadores bloqueiam fullscreen sem interação.
  useEffect(() => {
    const entrar = () => {
      document.documentElement.requestFullscreen?.().catch(() => {});
      window.removeEventListener('pointerdown', entrar);
      window.removeEventListener('keydown', entrar);
    };
    window.addEventListener('pointerdown', entrar);
    window.addEventListener('keydown', entrar);
    return () => {
      window.removeEventListener('pointerdown', entrar);
      window.removeEventListener('keydown', entrar);
    };
  }, []);

  const fullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const base = window.location.origin;
  const linkCliente = `${base}/torcida/${slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=10&data=${encodeURIComponent(linkCliente)}`;

  if (!dados) {
    return <div className="torc-tv"><div className="torc-tv-body"><div className="torc-tv-vazio">{erro || 'Carregando...'}</div></div></div>;
  }

  const loja = dados.loja;
  const premios = loja?.premios || {};
  const posAtual = idxFixo != null ? parseInt(idxFixo, 10) : Math.min(idx, slides.length - 1);
  const slideAtual = slides.length ? slides[((posAtual % slides.length) + slides.length) % slides.length] : null;
  const fundoUrl = slideAtual?.imagem_url ? `${API}${slideAtual.imagem_url}` : null;
  const ehArte = slideAtual?.tipo === 'arte';
  const ehRankingFundo = slideAtual?.tipo === 'ranking' && !!fundoUrl; // ranking com fundo proprio = tela cheia
  const fullscreenSlide = ehArte || ehRankingFundo;

  return (
    <div className="torc-tv">
      <div className="torc-tv-bg" aria-hidden="true" />
      {fullscreenSlide ? (
        // Arte (ou ranking com fundo proprio): cobre a tela inteira, sem header/faixa por cima
        ehArte
          ? <ModoArte url={fundoUrl} />
          : <ModoRanking ranking={dados.ranking} tipo={dados.ranking_tipo} proximo={dados.proximo_jogo} agora={agora} fundoUrl={fundoUrl} box={{ top: slideAtual.rk_top, left: slideAtual.rk_left, right: slideAtual.rk_right, bottom: slideAtual.rk_bottom }} />
      ) : (
        <>
          <div className="torc-tv-top">
            <div className="torc-tv-logo-badge">
              <img src={logoCentrao} alt="Centrão" className="torc-tv-logo-centrao" />
            </div>
            <div className="torc-tv-marca-center">
              <div className="torc-tv-marca-wrap">
                <span className="torc-tv-deco">⚽</span>
                <div className="torc-tv-marca"><span className="t1">Torcida</span> <span className="t2">Centrão</span></div>
                <span className="torc-tv-deco">🏆</span>
              </div>
              <div className="torc-tv-loja">📍 {loja?.nome}</div>
            </div>
            <div className="torc-tv-logo-badge">
              <img src={`${process.env.PUBLIC_URL}/logo-amanco.webp`} alt="Amanco Wavin" className="torc-tv-logo-amanco" />
            </div>
          </div>

          <div className="torc-tv-body">
            {slideAtual?.tipo === 'convite' && <ModoConvite qrUrl={qrUrl} premios={premios} />}
            {slideAtual?.tipo === 'ranking' && (
              <ModoRanking ranking={dados.ranking} tipo={dados.ranking_tipo} proximo={dados.proximo_jogo} agora={agora} fundoUrl={fundoUrl} box={{ top: slideAtual.rk_top, left: slideAtual.rk_left, right: slideAtual.rk_right, bottom: slideAtual.rk_bottom }} />
            )}
            {slideAtual?.tipo === 'resultado' && <ModoResultado resultado={dados.ultimo_resultado} />}
          </div>

          <div className="torc-tv-faixa" />
        </>
      )}
      <button className="torc-tv-fab" onClick={fullscreen}>⛶ Tela cheia</button>
    </div>
  );
}

// Arte (imagem) em tela cheia
function ModoArte({ url }) {
  if (!url) return <div className="torc-tv-vazio">Arte indisponível</div>;
  return <div className="torc-tv-arte"><img src={url} alt="" /></div>;
}

function ModoConvite({ qrUrl, premios }) {
  return (
    <div className="torc-tv-convite">
      <div className="torc-tv-qr"><img src={qrUrl} alt="QR Code da Torcida Centrão" /></div>
      <div className="torc-tv-convite-info">
        <h2>Participe e torça pelo Brasil! 🇧🇷</h2>
        <div className="torc-tv-passo"><span className="torc-tv-passo-num">1</span> Seja cliente cadastrado</div>
        <div className="torc-tv-passo"><span className="torc-tv-passo-num">2</span> Escaneie o QR Code</div>
        <div className="torc-tv-passo"><span className="torc-tv-passo-num">3</span> Palpite nos jogos e concorra</div>
        <div className="torc-tv-premios">
          <div className="pr">🥇 1º lugar: {premios.primeiro || 'A definir'}</div>
          <div className="pr">🥈 2º lugar: {premios.segundo || 'A definir'}</div>
          <div className="pr">🥉 3º lugar: {premios.terceiro || 'A definir'}</div>
        </div>
      </div>
    </div>
  );
}

function ModoRanking({ ranking, tipo, proximo, agora, fundoUrl, box }) {
  const top = (ranking || []).slice(0, 10);
  const titulo = tipo === 'funcionario' ? '🏆 Ranking de Funcionários' : '🏆 Ranking da Loja';
  const medalha = (p) => (p === 1 ? '🥇' : p === 2 ? '🥈' : p === 3 ? '🥉' : `${p}º`);
  const lista = top.length === 0
    ? <div className="torc-tv-vazio-card">Seja o primeiro a palpitar e aparecer aqui! ⚽</div>
    : top.map((r) => (
        <div className={`torc-tv-rank-row ${r.posicao <= 3 ? 'pos' + r.posicao : ''}`} key={r.participante_id}>
          <span className="torc-tv-rank-pos">{medalha(r.posicao)}</span>
          <span className="torc-tv-rank-nome">{r.nome_exibicao}</span>
          <span className="torc-tv-rank-pts">{r.total_pontos} <small>pts</small></span>
        </div>
      ));
  const proximoEl = proximo && (
    <div className="torc-tv-proximo">
      ⏱️ Próximo jogo: {proximo.time_a} x {proximo.time_b} — faltam {formatarCountdown(proximo.data_hora, agora)}
    </div>
  );
  // Com arte de fundo: a arte cobre tudo e o ranking preenche só o espaço delimitado dela.
  // As margens (caixa) vêm do ajuste do admin, pra encaixar no espaço branco da arte.
  if (fundoUrl) {
    const b = box || {};
    const cardStyle = {
      top: `${b.top ?? 20}%`, left: `${b.left ?? 6}%`,
      right: `${b.right ?? 6}%`, bottom: `${b.bottom ?? 6}%`,
    };
    return (
      <div className="torc-tv-ranking-fundo" style={{ backgroundImage: `url(${fundoUrl})` }}>
        <div className="torc-tv-card" style={cardStyle}>
          <div className="torc-tv-card-titulo">{titulo}</div>
          <div className="torc-tv-rank-lista">{lista}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="torc-tv-ranking">
      <div className="torc-tv-card">
        <div className="torc-tv-card-titulo">{titulo}</div>
        {lista}
      </div>
      {proximoEl}
    </div>
  );
}

function ModoResultado({ resultado }) {
  if (!resultado) return <div className="torc-tv-vazio">Aguardando o próximo resultado...</div>;
  const s = resultado.stats || {};
  return (
    <div className="torc-tv-resultado">
      <div style={{ fontSize: '2.2vw', fontWeight: 700 }}>Último jogo</div>
      <div className="torc-tv-placar">{resultado.jogo.label}</div>
      <div className="torc-tv-resultado-stats">
        🎯 {s.placar_exato || 0} cravaram o placar · ✅ {s.acertou_vencedor || 0} acertaram o vencedor
      </div>
      {resultado.acertaram_placar_exato?.length > 0 && (
        <div className="torc-tv-acertaram">
          Cravaram o placar: {resultado.acertaram_placar_exato.slice(0, 12).join(' · ')}
        </div>
      )}
    </div>
  );
}

// "02h 15min" | "1d 03h" | "já vai começar!"
function formatarCountdown(dataHora, agora) {
  const diff = new Date(dataHora).getTime() - agora;
  if (diff <= 0) return 'já vai começar!';
  const totalMin = Math.floor(diff / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) return `${Math.floor(h / 24)}d ${String(h % 24).padStart(2, '0')}h`;
  return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}min`;
}
